import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Google AdSense Component for React
 * 
 * Usage:
 * // Fixed size - recommended for sidebars (no layout shift)
 * <GoogleAdSense adSlot="3003488070" />
 * <GoogleAdSense adSlot="3003488070" width={336} height={280} />
 * 
 * // Responsive (Google decides size)
 * <GoogleAdSense adSlot="3003488070" adFormat="auto" fullWidthResponsive={true} />
 */
const GoogleAdSense = ({ 
  adSlot,
  adFormat = 'fixed',        // 'fixed' or 'auto'
  width = 300,               // Fixed width (used when adFormat='fixed')
  height = 250,              // Fixed height (used when adFormat='fixed')
  fullWidthResponsive = false,
  style = {},
}) => {
  const adRef = useRef(null);
  const isAdPushed = useRef(false);

  useEffect(() => {
    // Prevent pushing the same ad multiple times
    if (isAdPushed.current || !adRef.current) return;

    // Skip if AdSense already filled this <ins> (e.g. React StrictMode re-run)
    const ins = adRef.current.querySelector('ins.adsbygoogle');
    if (!ins || ins.getAttribute('data-adsbygoogle-status')) return;

    try {
      // Queue pattern: if the script hasn't loaded yet, this creates the
      // queue array and AdSense processes it once the script arrives.
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      isAdPushed.current = true;
    } catch (error) {
      console.error('AdSense initialization error:', error);
    }
  }, []);

  // Container styles based on format
  const containerStyles = adFormat === 'fixed' 
    ? {
        width: width,
        height: height,
        maxWidth: '100%',
        margin: '0 auto',
      }
    : {
        width: '100%',
        minHeight: height,
      };

  // Ad element styles based on format
  const adStyles = adFormat === 'fixed'
    ? {
        display: 'inline-block',
        width: width,
        height: height,
      }
    : {
        display: 'block',
        width: '100%',
        minHeight: height,
      };

  // Show placeholder in development mode
  if (process.env.NODE_ENV === 'development') {
    return (
      <Box
        sx={{
          ...containerStyles,
          bgcolor: 'grey.100',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'grey.300',
          gap: 1,
          ...style
        }}
      >
        <Typography variant="body2" color="text.secondary">
          📢 Ad Space
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {adFormat === 'fixed' ? `${width}x${height}` : 'Responsive'}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          (Slot: {adSlot})
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={adRef}
      sx={{
        ...containerStyles,
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'grey.50',
        borderRadius: 1,
        ...style
      }}
    >
      <ins
        className="adsbygoogle"
        style={adStyles}
        data-ad-client="ca-pub-7646838147548913"
        data-ad-slot={adSlot}
        {...(adFormat === 'auto' && {
          'data-ad-format': 'auto',
          'data-full-width-responsive': fullWidthResponsive.toString(),
        })}
      />
    </Box>
  );
};

export default GoogleAdSense;